import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, CreditCard, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getCart, updateCartQuantity, removeFromCart, getCartTotal, clearCart } from '../mock/mockData';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Cart = () => {
  const [cart, setCart] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadCart();
    window.addEventListener('cartUpdated', loadCart);
    return () => window.removeEventListener('cartUpdated', loadCart);
  }, []);

  const loadCart = () => {
    setCart(getCart());
  };

  const handleQuantityChange = (productId, newQuantity) => {
    updateCartQuantity(productId, newQuantity);
    loadCart();
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const handleRemove = (productId) => {
    removeFromCart(productId);
    loadCart();
    window.dispatchEvent(new Event('cartUpdated'));
    toast.success('Item removed from cart');
  };

  const handleCheckout = async () => {
    setIsProcessing(true);
    
    try {
      const originUrl = window.location.origin;
      
      // Prepare cart items for checkout
      const items = cart.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity
      }));
      
      const response = await axios.post(`${BACKEND_URL}/api/payments/cart-checkout`, {
        items: items,
        subtotal: total,
        shipping: shipping,
        total: finalTotal,
        origin_url: originUrl
      });

      if (response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setIsProcessing(false);
    }
  };

  const total = getCartTotal();
  const shipping = total > 100 ? 0 : 12.99;
  const finalTotal = total + shipping;

  if (cart.length === 0) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        {/* Patriotic background */}
        <div 
          className="fixed inset-0 opacity-5"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1563804846955-f87a66f0b20a?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }}
        ></div>
        <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-blue-50/40 to-red-50/40"></div>
        <div className="text-center relative z-10">
          <ShoppingBag className="w-24 h-24 text-slate-300 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-slate-900 mb-2">Your Cart is Empty</h2>
          <p className="text-slate-600 mb-6">Add some products to get started!</p>
          <Button onClick={() => navigate('/us-flags')} className="bg-red-600 hover:bg-red-700">
            Shop Now
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden py-12">
      {/* Patriotic background */}
      <div 
        className="fixed inset-0 opacity-5"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1563804846955-f87a66f0b20a?w=1920)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      ></div>
      <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-blue-50/40 to-red-50/40"></div>
      <div className="container mx-auto px-4 relative z-10">
        <h1 className="text-4xl font-bold text-slate-900 mb-8">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <Card key={item.id} data-testid={`cart-item-${item.id}`}>
                <CardContent className="p-6">
                  <div className="flex gap-6">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-32 h-32 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h3 className="font-bold text-xl text-slate-900 mb-2">{item.name}</h3>
                      <p className="text-slate-600 text-sm mb-4">{item.category}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Button
                            size="icon"
                            variant="outline"
                            onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            data-testid={`decrease-qty-${item.id}`}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="font-semibold text-lg w-8 text-center">{item.quantity}</span>
                          <Button
                            size="icon"
                            variant="outline"
                            onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                            data-testid={`increase-qty-${item.id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-red-600">${(item.price * item.quantity).toFixed(2)}</div>
                          <div className="text-sm text-slate-600">${item.price} each</div>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemove(item.id)}
                      className="hover:bg-red-50 hover:text-red-600"
                      data-testid={`remove-item-${item.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Order Summary */}
          <div>
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Order Summary</h2>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal</span>
                    <span className="font-semibold">${total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Shipping</span>
                    <span className="font-semibold">
                      {shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}
                    </span>
                  </div>
                  {total < 100 && (
                    <p className="text-sm text-blue-600">
                      Add ${(100 - total).toFixed(2)} more for free shipping!
                    </p>
                  )}
                  <div className="border-t pt-3 flex justify-between text-lg font-bold text-slate-900">
                    <span>Total</span>
                    <span className="text-red-600">${finalTotal.toFixed(2)}</span>
                  </div>
                </div>

                <Button
                  onClick={handleCheckout}
                  disabled={isProcessing}
                  className="w-full bg-red-600 hover:bg-red-700 text-lg py-6 mb-3"
                  data-testid="checkout-btn"
                >
                  {isProcessing ? 'Processing...' : 'Pay with Card'}
                </Button>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-slate-500">or</span>
                  </div>
                </div>

                <a
                  href={`https://venmo.com/coastalflagco?txn=pay&amount=${finalTotal.toFixed(2)}&note=Order%20from%20CoastalFlagCo.com`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full"
                >
                  <Button
                    type="button"
                    className="w-full bg-[#008CFF] hover:bg-[#0070CC] text-white text-lg py-6 mb-3"
                    data-testid="venmo-btn"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.5 3c.9 1.5 1.3 3 1.3 5 0 5.6-4.8 12.8-8.7 17.9H4.8L2 3.5l6.2-.6 1.5 12.1c1.4-2.3 3.1-5.9 3.1-8.4 0-1.9-.3-3.2-.9-4.3L19.5 3z"/>
                    </svg>
                    Pay with Venmo
                  </Button>
                </a>

                <p className="text-xs text-center text-slate-500 mb-4">
                  After Venmo payment, we'll confirm your order via email
                </p>

                <div className="flex items-center justify-center gap-2 text-sm text-slate-600 mb-4">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span>Secure payments</span>
                </div>

                <Button
                  variant="outline"
                  onClick={() => navigate('/us-flags')}
                  className="w-full"
                  data-testid="continue-shopping-btn"
                >
                  Continue Shopping
                </Button>

                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <h3 className="font-semibold text-sm text-slate-900 mb-2">Why Shop With Us?</h3>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>✓ Made in USA</li>
                    <li>✓ Free shipping over $100</li>
                    <li>✓ Premium quality guaranteed</li>
                    <li>✓ Expert customer service</li>
                  </ul>
                </div>

                <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs text-slate-500">
                  <span className="px-2 py-1 bg-slate-100 rounded">Visa</span>
                  <span className="px-2 py-1 bg-slate-100 rounded">Mastercard</span>
                  <span className="px-2 py-1 bg-slate-100 rounded">Amex</span>
                  <span className="px-2 py-1 bg-blue-600 text-white rounded">Venmo</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
